/*eslint-disable*/
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
// javascript plugin used to create scrollbars on windows
import PerfectScrollbar from "perfect-scrollbar";
import { NavLink, useLocation } from "react-router-dom";
import cx from "classnames";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Hidden from "@material-ui/core/Hidden";
import Collapse from "@material-ui/core/Collapse";
import Icon from "@material-ui/core/Icon";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import GroupIcon from "@mui/icons-material/Group";

// @material-ui/icons
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InstallMobileOutlinedIcon from "@mui/icons-material/InstallMobileOutlined";

import { beramourSecondary } from "../../theme/Colors";

// core components
import AdminNavbarLinks from "components/Navbars/AdminNavbarLinks.js";
import SimpleAlert from "../AtutaAlerts/CustomAlerts";

import sidebarStyle from "assets/jss/material-dashboard-pro-react/components/sidebarStyle.js";
import { useUnread } from "../../context/UnreadContext";
import Configs from "../../utils/Configs";
import { useAuth } from "../../context/AuthContext";
import LogoutApi from "../../api_calls/LogoutApi";
import ProfileDetails from "../../api_calls/ProfileDetailsApi";

import avatar from "assets/img/faces/user-profile-icon.jpg";

const useStyles = makeStyles(sidebarStyle);

var ps;

function SidebarWrapper({ className, user, headerLinks, links }) {
  const sidebarWrapper = React.useRef();

  React.useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1 && sidebarWrapper.current) {
      ps = new PerfectScrollbar(sidebarWrapper.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
    }

    return () => {
      if (
        navigator.platform.indexOf("Win") > -1 &&
        ps &&
        typeof ps.destroy === "function"
      ) {
        ps.destroy();
      }
    };
  }, []);

  return (
    <div
      className={className}
      ref={sidebarWrapper}
      style={{
        height: "calc(100% - 80px)",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {user}
      {headerLinks}
      {links}
    </div>
  );
}

function Sidebar(props) {
  const mainPanel = React.useRef();
  const { user: authUser, logout, profilePhotoVersion } = useAuth();
  const { unreadCount } = useUnread();
  const classes = useStyles();

  const [profileData, setProfileData] = useState(null);
  const [miniActive, setMiniActive] = React.useState(true);
  const [openAvatar, setOpenAvatar] = React.useState(false);
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [installMessage, setInstallMessage] = React.useState("");
  const [state, setState] = React.useState({});

  let location = useLocation();
  const profileImageUrl = profileData?.profile_image || authUser?.profile_image;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!authUser?.user_id) return;
      try {
        const data = await ProfileDetails.fetchProfileDetails(authUser.user_id);
        if (data) setProfileData(data);
      } catch (error) {
        console.error("Failed to fetch profile details in Sidebar", error);
      }
    };

    if (
      !profileData ||
      authUser?.profile_image === Configs.noPhotoUrl ||
      profilePhotoVersion > 0 // profile photo just changed: show the new one
    ) {
      fetchProfile();
    }
  }, [authUser?.user_id, profilePhotoVersion]);

  React.useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  React.useEffect(() => {
    setState(getCollapseStates(props.routes));
  }, []);

  const getCollapseStates = (routes) => {
    let initialState = {};
    routes.map((prop) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
  };

  const handleLogout = async () => {
    try {
      if (authUser?.user_id) {
        await LogoutApi.logout(authUser.user_id);
      }
    } catch (error) {
      console.error("Error calling logout:", error);
    } finally {
      logout();
      if (window.innerWidth <= 960) {
        props.handleDrawerToggle?.();
      }
    }
  };

  const getCollapseInitialState = (routes) => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && getCollapseInitialState(routes[i].views)) {
        return true;
      } else if (location.pathname === routes[i].layout + routes[i].path) {
        return true;
      }
    }
    return false;
  };

  const activeRoute = (routeName) => {
    return location.pathname === routeName ? "active" : "";
  };

  const createLinks = (routes) => {
    // ROLE RESTRICTION: Hide middle dynamic routes for roles 4 and 5
    if (authUser?.userrole === "4" || authUser?.userrole === "5") {
      return null;
    }

    const {
      color,
      rtlActive,
      handleDrawerToggle,
      miniActive: propMiniActive,
    } = props;

    return routes.map((prop, key) => {
      let displayName = rtlActive ? prop.rtlName : prop.name;

      if (prop.path === "/chat/" && unreadCount > 0) {
        displayName = `Messages (${unreadCount})`;
      }

      if (prop.redirect || prop.showInSidebar === false) return null;

      if (
        (prop.name === "Administration" || prop.name === "Billing") &&
        authUser?.userrole !== "1"
      )
        return null;

      if (prop.collapse) {
        var st = {};
        st[prop["state"]] = !state[prop.state];
        const navLinkClasses =
          classes.itemLink +
          " " +
          cx({
            [" " + classes.collapseActive]: getCollapseInitialState(prop.views),
          });
        const itemText =
          classes.itemText +
          " " +
          cx({
            [classes.itemTextMini]: propMiniActive && miniActive,
            [classes.itemTextMiniRTL]:
              rtlActive && propMiniActive && miniActive,
            [classes.itemTextRTL]: rtlActive,
          });
        const collapseItemText =
          classes.collapseItemText +
          " " +
          cx({
            [classes.collapseItemTextMini]: propMiniActive && miniActive,
            [classes.collapseItemTextMiniRTL]:
              rtlActive && propMiniActive && miniActive,
            [classes.collapseItemTextRTL]: rtlActive,
          });
        const itemIcon =
          classes.itemIcon + " " + cx({ [classes.itemIconRTL]: rtlActive });
        const caret =
          classes.caret + " " + cx({ [classes.caretRTL]: rtlActive });
        const collapseItemMini =
          classes.collapseItemMini +
          " " +
          cx({ [classes.collapseItemMiniRTL]: rtlActive });

        return (
          <ListItem
            key={key}
            className={cx(
              { [classes.item]: prop.icon !== undefined },
              { [classes.collapseItem]: prop.icon === undefined },
            )}
          >
            <NavLink
              to={prop.path || "#"}
              className={navLinkClasses}
              onClick={(e) => {
                if (prop.collapse) {
                  e.preventDefault();
                  setState(st);
                }
              }}
            >
              {prop.icon !== undefined ? (
                typeof prop.icon === "string" ? (
                  <Icon className={itemIcon}>{prop.icon}</Icon>
                ) : (
                  <prop.icon className={itemIcon} />
                )
              ) : (
                <span className={collapseItemMini}>
                  {rtlActive ? prop.rtlMini : prop.mini}
                </span>
              )}
              <ListItemText
                primary={displayName}
                secondary={
                  prop.collapse && (
                    <b
                      className={
                        caret +
                        " " +
                        (state[prop.state] ? classes.caretActive : "")
                      }
                    />
                  )
                }
                disableTypography={true}
                className={cx(
                  { [itemText]: prop.icon !== undefined },
                  { [collapseItemText]: prop.icon === undefined },
                )}
              />
            </NavLink>
            <Collapse in={state[prop.state]} unmountOnExit>
              <List className={classes.list + " " + classes.collapseList}>
                {prop.views && createLinks(prop.views)}
              </List>
            </Collapse>
          </ListItem>
        );
      }

      const innerNavLinkClasses =
        classes.collapseItemLink +
        " " +
        cx({ [" " + classes[color]]: activeRoute(prop.layout + prop.path) });
      const collapseItemMini =
        classes.collapseItemMini +
        " " +
        cx({ [classes.collapseItemMiniRTL]: rtlActive });
      const navLinkClasses =
        classes.itemLink +
        " " +
        cx({ [" " + classes[color]]: activeRoute(prop.layout + prop.path) });
      const itemText =
        classes.itemText +
        " " +
        cx({
          [classes.itemTextMini]: propMiniActive && miniActive,
          [classes.itemTextMiniRTL]: rtlActive && propMiniActive && miniActive,
          [classes.itemTextRTL]: rtlActive,
        });
      const collapseItemText =
        classes.collapseItemText +
        " " +
        cx({
          [classes.collapseItemTextMini]: propMiniActive && miniActive,
          [classes.collapseItemTextMiniRTL]:
            rtlActive && propMiniActive && miniActive,
          [classes.collapseItemTextRTL]: rtlActive,
        });
      const itemIcon =
        classes.itemIcon + " " + cx({ [classes.itemIconRTL]: rtlActive });

      return (
        <ListItem
          key={key}
          className={cx(
            { [classes.item]: prop.icon !== undefined },
            { [classes.collapseItem]: prop.icon === undefined },
          )}
        >
          <NavLink
            to={prop.layout + prop.path}
            onClick={() => {
              if (window.innerWidth <= 960) handleDrawerToggle?.();
              else setMiniActive(true);
            }}
            className={cx(
              { [navLinkClasses]: prop.icon !== undefined },
              { [innerNavLinkClasses]: prop.icon === undefined },
            )}
          >
            {prop.icon !== undefined ? (
              typeof prop.icon === "string" ? (
                <Icon className={itemIcon}>{prop.icon}</Icon>
              ) : (
                <prop.icon className={itemIcon} />
              )
            ) : (
              <span className={collapseItemMini}>
                {rtlActive ? prop.rtlMini : prop.mini}
              </span>
            )}
            <ListItemText
              primary={displayName}
              disableTypography={true}
              className={cx(
                { [itemText]: prop.icon !== undefined },
                { [collapseItemText]: prop.icon === undefined },
              )}
            />
          </NavLink>
        </ListItem>
      );
    });
  };

  const { logo, image, logoText, routes, bgColor, rtlActive, color } = props;
  const itemText =
    classes.itemText +
    " " +
    cx({
      [classes.itemTextMini]: props.miniActive && miniActive,
      [classes.itemTextMiniRTL]: rtlActive && props.miniActive && miniActive,
      [classes.itemTextRTL]: rtlActive,
    });
  const collapseItemText =
    classes.collapseItemText +
    " " +
    cx({
      [classes.collapseItemTextMini]: props.miniActive && miniActive,
      [classes.collapseItemTextMiniRTL]:
        rtlActive && props.miniActive && miniActive,
      [classes.collapseItemTextRTL]: rtlActive,
    });
  const userWrapperClass =
    classes.user + " " + cx({ [classes.whiteAfter]: bgColor === "white" });
  const caret = classes.caret + " " + cx({ [classes.caretRTL]: rtlActive });
  const collapseItemMini =
    classes.collapseItemMini +
    " " +
    cx({ [classes.collapseItemMiniRTL]: rtlActive });
  const photo = classes.photo + " " + cx({ [classes.photoRTL]: rtlActive });
  const itemIcon =
    classes.itemIcon + " " + cx({ [classes.itemIconRTL]: rtlActive });

  var user = (
    <div className={userWrapperClass}>
      <div className={photo}>
        <img
          src={
            profileImageUrl &&
            profileImageUrl !== Configs.noPhotoUrl
              ? profileImageUrl
              : avatar
          }
          className={classes.avatarImg}
          alt="User Profile"
        />
      </div>
      <List className={classes.list}>
        <ListItem className={classes.item + " " + classes.userItem}>
          <NavLink
            to={"#"}
            className={classes.itemLink + " " + classes.userCollapseButton}
            onClick={() => setOpenAvatar(!openAvatar)}
          >
            <ListItemText
              primary={
                rtlActive ? "تانيا أندرو" : `Hello ${authUser?.first_name}`
              }
              secondary={
                <b
                  className={
                    caret +
                    " " +
                    classes.userCaret +
                    " " +
                    (openAvatar ? classes.caretActive : "")
                  }
                />
              }
              disableTypography={true}
              className={itemText + " " + classes.userItemText}
            />
          </NavLink>
          <Collapse in={openAvatar} unmountOnExit>
            <List className={classes.list + " " + classes.collapseList}>
              <ListItem className={classes.collapseItem}>
                {/* ✅ CONDITIONALLY CHANGE LINK FOR ROLES 4 & 5 */}
                {authUser?.userrole === "4" || authUser?.userrole === "5" ? (
                  <NavLink
                    to={`/singles/marketer-profile/${authUser?.user_id}`}
                    className={
                      classes.itemLink + " " + classes.userCollapseLinks
                    }
                    onClick={() => {
                      if (window.innerWidth <= 960)
                        props.handleDrawerToggle?.();
                    }}
                  >
                    <span className={collapseItemMini}>
                      <AccountCircleOutlinedIcon style={{ fontSize: "16px" }} />
                    </span>
                    <ListItemText
                      primary={
                        rtlActive ? "إعدادات المسوق" : "Marketer Settings"
                      }
                      disableTypography={true}
                      className={collapseItemText}
                    />
                  </NavLink>
                ) : (
                  <NavLink
                    to={`/singles/my-profile/${authUser?.user_id}`}
                    className={
                      classes.itemLink + " " + classes.userCollapseLinks
                    }
                    onClick={() => {
                      if (window.innerWidth <= 960)
                        props.handleDrawerToggle?.();
                    }}
                  >
                    <span className={collapseItemMini}>
                      <AccountCircleOutlinedIcon style={{ fontSize: "16px" }} />
                    </span>
                    <ListItemText
                      primary={rtlActive ? "ملفي" : "Profile Settings"}
                      disableTypography={true}
                      className={collapseItemText}
                    />
                  </NavLink>
                )}
              </ListItem>
              <ListItem className={classes.collapseItem}>
                <NavLink
                  to={`/singles/downlines/${authUser?.user_id}`}
                  className={classes.itemLink + " " + classes.userCollapseLinks}
                  onClick={() => {
                    if (window.innerWidth <= 960) props.handleDrawerToggle?.();
                  }}
                >
                  <span className={collapseItemMini}>
                    <PeopleAltOutlinedIcon style={{ fontSize: "16px" }} />
                  </span>
                  <ListItemText
                    primary={rtlActive ? "My Referrals" : "My Referrals"}
                    disableTypography={true}
                    className={collapseItemText}
                  />
                </NavLink>
              </ListItem>

              {authUser?.userrole === "4" && (
                <ListItem className={classes.collapseItem}>
                  <NavLink
                    to={`/singles/create-affiliate-marketer`}
                    className={
                      classes.itemLink + " " + classes.userCollapseLinks
                    }
                    onClick={() => {
                      if (window.innerWidth <= 960)
                        props.handleDrawerToggle?.();
                    }}
                  >
                    <span className={collapseItemMini}>
                      <GroupIcon style={{ fontSize: "16px" }} />
                    </span>
                    <ListItemText
                      primary={
                        rtlActive
                          ? "Create Affiliate Marketer"
                          : "Create Affiliate Marketer"
                      }
                      disableTypography={true}
                      className={collapseItemText}
                    />
                  </NavLink>
                </ListItem>
              )}

              {authUser?.userrole === "1" && (
                <ListItem className={classes.collapseItem}>
                  <NavLink
                    to={`/singles/create-tl-marketer`}
                    className={
                      classes.itemLink + " " + classes.userCollapseLinks
                    }
                    onClick={() => {
                      if (window.innerWidth <= 960)
                        props.handleDrawerToggle?.();
                    }}
                  >
                    <span className={collapseItemMini}>
                      <GroupIcon style={{ fontSize: "16px" }} />
                    </span>
                    <ListItemText
                      primary={
                        rtlActive ? "Create TL Marketer" : "Create TL Marketer"
                      }
                      disableTypography={true}
                      className={collapseItemText}
                    />
                  </NavLink>
                </ListItem>
              )}
              <ListItem className={classes.collapseItem}>
                <NavLink
                  to={`/singles/change-password/`}
                  className={classes.itemLink + " " + classes.userCollapseLinks}
                  onClick={() => {
                    if (window.innerWidth <= 960) props.handleDrawerToggle?.();
                  }}
                >
                  <span className={collapseItemMini}>
                    <LockOutlinedIcon style={{ fontSize: "16px" }} />
                  </span>
                  <ListItemText
                    primary={rtlActive ? "Change Password" : "Change Password"}
                    disableTypography={true}
                    className={collapseItemText}
                  />
                </NavLink>
              </ListItem>
              <ListItem className={classes.collapseItem}>
                <NavLink
                  to={"#"}
                  className={classes.itemLink + " " + classes.userCollapseLinks}
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.innerWidth <= 960) props.handleDrawerToggle?.();
                    handleInstallClick();
                  }}
                >
                  <span className={collapseItemMini}>
                    <InstallMobileOutlinedIcon style={{ fontSize: "16px" }} />
                  </span>
                  <ListItemText
                    primary={rtlActive ? "Install App" : "Install App"}
                    disableTypography={true}
                    className={collapseItemText}
                  />
                </NavLink>
              </ListItem>
            </List>
          </Collapse>
        </ListItem>
      </List>
    </div>
  );

  var links = (
    <List className={classes.list} style={{ paddingBottom: "100px" }}>
      {createLinks(routes)}
      <ListItem className={classes.item}>
        <a
          href={Configs.supportUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.itemLink}
        >
          <HelpOutlineIcon className={itemIcon} />
          <ListItemText
            primary={rtlActive ? "مركز المساعدة" : "Support Center"}
            disableTypography={true}
            className={itemText}
          />
        </a>
      </ListItem>

      <ListItem className={classes.item}>
        <NavLink
          to="#"
          className={classes.itemLink}
          onClick={(e) => {
            e.preventDefault();
            handleLogout();
          }}
        >
          <ExitToAppOutlinedIcon className={itemIcon} />
          <ListItemText
            primary={rtlActive ? "تسجيل الخروج" : "Logout"}
            disableTypography={true}
            className={itemText}
          />
        </NavLink>
      </ListItem>
    </List>
  );

  const logoNormal =
    classes.logoNormal +
    " " +
    cx({
      [classes.logoNormalSidebarMini]: props.miniActive && miniActive,
      [classes.logoNormalSidebarMiniRTL]:
        rtlActive && props.miniActive && miniActive,
      [classes.logoNormalRTL]: rtlActive,
    });
  const logoMini =
    classes.logoMini + " " + cx({ [classes.logoMiniRTL]: rtlActive });
  const logoClasses =
    classes.logo + " " + cx({ [classes.whiteAfter]: bgColor === "white" });

  var brand = (
    <div className={logoClasses}>
      <a href="#" className={logoMini}>
        <img src={logo} alt="logo" className={classes.img} />
      </a>
      <a href="#" className={logoNormal}>
        {logoText}
      </a>
    </div>
  );

  const drawerPaper =
    classes.drawerPaper +
    " " +
    cx({
      [classes.drawerPaperMini]: props.miniActive && miniActive,
      [classes.drawerPaperRTL]: rtlActive,
    });
  const sidebarWrapper =
    classes.sidebarWrapper +
    " " +
    cx({
      [classes.drawerPaperMini]: props.miniActive && miniActive,
      [classes.sidebarWrapperWithPerfectScrollbar]:
        navigator.platform.indexOf("Win") > -1,
    });

  return (
    <div ref={mainPanel}>
      <Hidden mdUp implementation="css">
        <Drawer
          variant="temporary"
          anchor={rtlActive ? "left" : "right"}
          open={props.open}
          classes={{
            paper: drawerPaper + " " + classes[bgColor + "Background"],
          }}
          onClose={props.handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
        >
          {brand}
          <SidebarWrapper
            className={sidebarWrapper}
            user={user}
            headerLinks={<AdminNavbarLinks rtlActive={rtlActive} />}
            links={links}
          />
          {image !== undefined ? (
            <div
              className={classes.background}
              style={{ backgroundImage: "url(" + image + ")" }}
            />
          ) : null}
        </Drawer>
      </Hidden>
      <Hidden smDown implementation="css">
        <Drawer
          onMouseOver={() => setMiniActive(false)}
          onMouseOut={() => setMiniActive(true)}
          anchor={rtlActive ? "right" : "left"}
          variant="permanent"
          open
          classes={{
            paper: drawerPaper + " " + classes[bgColor + "Background"],
          }}
        >
          {brand}
          <SidebarWrapper
            className={sidebarWrapper}
            user={user}
            links={links}
          />
          {image !== undefined ? (
            <div
              className={classes.background}
              style={{ backgroundImage: "url(" + image + ")" }}
            />
          ) : null}
        </Drawer>
      </Hidden>
      {installMessage && (
        <SimpleAlert
          message={installMessage}
          onClose={() => setInstallMessage("")}
        />
      )}
    </div>
  );
}

Sidebar.defaultProps = { bgColor: "black" };
Sidebar.propTypes = {
  bgColor: PropTypes.oneOf(["white", "black", "blue", "red"]),
  rtlActive: PropTypes.bool,
  color: PropTypes.oneOf([
    "white",
    "red",
    "orange",
    "green",
    "blue",
    "purple",
    "rose",
  ]),
  logo: PropTypes.string,
  logoText: PropTypes.string,
  image: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object),
  miniActive: PropTypes.bool,
  open: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
};

export default Sidebar;
